import {
  Component,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { LucideAngularModule } from 'lucide-angular';
import {
  DatepickerCloseHandler,
  DatepickerCustomHeaderComponent,
  DATEPICKER_CLOSE_HANDLER
} from './datepicker-custom-header.component';

export type DatepickerConstraintMode = 'past' | 'future' | 'all';
export type DatepickerVariant = 'field' | 'inline';

@Component({
  selector: 'app-datepicker',
  standalone: true,
  imports: [
    CommonModule,
    MatDatepickerModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    LucideAngularModule,
    DatepickerCustomHeaderComponent
  ],
  providers: [
    { provide: DATEPICKER_CLOSE_HANDLER, useExisting: forwardRef(() => DatepickerComponent) }
  ],
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DatepickerComponent implements DatepickerCloseHandler {
  @ViewChild('pickerWithHeader') private pickerWithHeader?: MatDatepicker<Date>;
  @ViewChild('pickerInlineWithHeader') private pickerInlineWithHeader?: MatDatepicker<Date>;
  @Input() dataSelecionada: Date | null = null;
  @Input() maxDate?: Date;
  @Input() minDate?: Date;
  @Input() placeholder = 'Selecione a data';
  @Input() disabled = false;
  @Input() label: string | null = null;
  @Input() touchUi = true;
  @Input() required = false;
  @Input() fieldClass = 'w-full';
  @Input() inlineContainerClass = '';
  @Input() variant: DatepickerVariant = 'field';
  
  @Input() referenceDate?: Date;
  @Input() dateConstraintMode: DatepickerConstraintMode = 'all';
  @Input() showClearIcon = false;
  @Input() showActions = false;
  @Input() showCloseButton = true;

  readonly customHeader = DatepickerCustomHeaderComponent;

  @Output() dateChange = new EventEmitter<Date | null>();
  @Output() dateSelected = new EventEmitter<Date | null>();
  @Output() closed = new EventEmitter<void>();

  get effectiveMinDate(): Date | null {
    if (this.minDate) return this.minDate;
    return this.getModeMinDate();
  }

  get effectiveMaxDate(): Date | null {
    if (this.maxDate) return this.maxDate;
    return this.getModeMaxDate();
  }

  dateFilter = (date: Date | null): boolean => {
    if (!date) return false;

    const normalizedDate = this.normalizeDate(date);
    const effectiveMinDate = this.effectiveMinDate ? this.normalizeDate(this.effectiveMinDate) : null;
    const effectiveMaxDate = this.effectiveMaxDate ? this.normalizeDate(this.effectiveMaxDate) : null;

    if (effectiveMinDate && normalizedDate < effectiveMinDate) {
      return false;
    }

    if (effectiveMaxDate && normalizedDate > effectiveMaxDate) {
      return false;
    }

    return true;
  };

  onDateChange(data: Date | null): void {
    this.dataSelecionada = data;
    this.dateChange.emit(data);
    this.dateSelected.emit(data);
  }

  setDataHoje(picker: MatDatepicker<Date>, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    const hoje = new Date();
    if (!this.dateFilter(hoje)) {
      return;
    }
    
    this.applyDateAndSyncPicker(hoje, picker);
  }

  clearData(picker: MatDatepicker<Date>, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    this.applyDateAndSyncPicker(null, picker);
  }

  onPickerClosed(): void {
    this.closed.emit();
  }

  closeActivePicker(): void {
    if (this.pickerWithHeader?.opened) {
      this.pickerWithHeader.close();
    } else if (this.pickerInlineWithHeader?.opened) {
      this.pickerInlineWithHeader.close();
    }
  }

  private applyDateAndSyncPicker(data: Date | null, picker: MatDatepicker<Date>): void {
    this.onDateChange(data);
    picker.close();
  }

  private getModeMinDate(): Date | null {
    if (this.dateConstraintMode === 'future' && this.referenceDate) {
      return this.referenceDate;
    }
    return null;
  }

  private getModeMaxDate(): Date | null {
    if (this.dateConstraintMode === 'past' && this.referenceDate) {
      return this.referenceDate;
    }
    return null;
  }

  private normalizeDate(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  }
}
